import React, { useState } from 'react';
import { useLocalization } from '../contexts/LocalizationContext';
import { useAuth } from '../contexts/AuthContext';
import { getAssetUrl } from '../services/api';
import GuestNavbar from './GuestNavbar';

const AuthForms = () => {
  const { t, language, changeLanguage, availableLanguages } = useLocalization();
  const { login, signup } = useAuth();
  const [isLoginMode, setIsLoginMode] = useState(true);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Form data
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    confirmPassword: ''
  });

  // Form validation errors
  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = t('validation.required');
    } else if (!validateEmail(formData.email)) {
      newErrors.email = t('validation.emailInvalid');
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = t('validation.required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('validation.passwordMinLength');
    }

    // Additional validation for signup
    if (!isLoginMode) {
      if (!formData.firstName) {
        newErrors.firstName = t('validation.required');
      }
      if (!formData.lastName) {
        newErrors.lastName = t('validation.required');
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = t('validation.required');
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = t('validation.passwordMatch');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const showMessage = (text, type = 'info') => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => {
      setMessage('');
      setMessageType('');
    }, 5000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      if (isLoginMode) {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          showMessage(t('messages.loginSuccess'), 'success');
        } else {
          if (result.error.includes('confirm')) {
            showMessage(t('messages.emailConfirmationRequired'), 'error');
          } else if (result.error.includes('Invalid')) {
            showMessage(t('messages.invalidCredentials'), 'error');
          } else {
            showMessage(result.error, 'error');
          }
        }
      } else {
        const result = await signup({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password: formData.password
        });
        
        if (result.success) {
          showMessage(t('messages.signupSuccess'), 'success');
          setIsLoginMode(true);
          setFormData({
            email: formData.email, // Keep email for login
            password: '',
            firstName: '',
            lastName: '',
            confirmPassword: ''
          });
        } else {
          showMessage(result.error, 'error');
        }
      }
    } catch (error) {
      showMessage(t('messages.networkError'), 'error');
    }

    setLoading(false);
  };

  const switchMode = () => {
    setIsLoginMode(!isLoginMode);
    setFormData({
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      confirmPassword: ''
    });
    setErrors({});
    setMessage('');
    setShowPassword(false);
    setShowConfirmPassword(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const toggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <div className="content-wrapper">
      {/* Header */}
      <GuestNavbar 
        showGetStarted={false} 
        showAbout={true}
      />

      {/* Hero Section */}
      <section className="wrapper image-wrapper bg-image bg-overlay bg-overlay-light-600 text-white" style={{ 
        backgroundImage: `url(${getAssetUrl('/assets/img/photos/bg18.png')})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}>
        <div className="container pt-17 pb-20 pt-md-19 pb-md-21 text-center">
          <div className="row">
            <div className="col-lg-8 mx-auto">
              <h1 className="display-1 mb-3">
                {isLoginMode ? t('auth.login') : t('auth.signup')}
              </h1>
              <nav className="d-inline-block" aria-label="breadcrumb">
                <ol className="breadcrumb">
                  <li className="breadcrumb-item">
                    <button className="border-0 bg-transparent text-decoration-underline p-0" onClick={() => window.location.reload()}>{t('navigation.home')}</button>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    {isLoginMode ? t('auth.login') : t('auth.signup')}
                  </li>
                </ol>
              </nav>
            </div>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="wrapper bg-light">
        <div className="container pb-14 pb-md-16">
          <div className="row">
            <div className="col-lg-6 col-xl-5 col-xxl-5 mx-auto mt-n20">
              <div className="card">
                <div className="card-body p-11 text-center">
                  <h2 className="mb-3 text-start">
                    {isLoginMode ? t('auth.loginTitle') : t('auth.signupTitle')}
                  </h2>
                  <p className="lead mb-6 text-start">
                    {isLoginMode 
                      ? t('auth.loginDescription')
                      : t('auth.signupDescription')
                    }
                  </p>

                  {message && (
                    <div className={`alert alert-${messageType === 'success' ? 'success' : messageType === 'error' ? 'danger' : 'info'} mb-4`}>
                      {message}
                    </div>
                  )}

                  <form className="text-start mb-3" onSubmit={handleSubmit}>
                    {!isLoginMode && (
                      <>
                        <div className="form-floating mb-4">
                          <input
                            type="text"
                            className={`form-control ${errors.firstName ? 'is-invalid' : ''}`}
                            placeholder={t('auth.firstName')}
                            id="firstName"
                            name="firstName"
                            value={formData.firstName}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="firstName">{t('auth.firstName')}</label>
                          {errors.firstName && <div className="invalid-feedback">{errors.firstName}</div>}
                        </div>

                        <div className="form-floating mb-4">
                          <input
                            type="text"
                            className={`form-control ${errors.lastName ? 'is-invalid' : ''}`}
                            placeholder={t('auth.lastName')}
                            id="lastName"
                            name="lastName"
                            value={formData.lastName}
                            onChange={handleInputChange}
                          />
                          <label htmlFor="lastName">{t('auth.lastName')}</label>
                          {errors.lastName && <div className="invalid-feedback">{errors.lastName}</div>}
                        </div>
                      </>
                    )}

                    <div className="form-floating mb-4">
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        placeholder={t('auth.email')}
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                      />
                      <label htmlFor="email">{t('auth.email')}</label>
                      {errors.email && <div className="invalid-feedback">{errors.email}</div>}
                    </div>

                    <div className="form-floating password-field mb-4">
                      <input
                        type={showPassword ? "text" : "password"}
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        placeholder={t('auth.password')}
                        id="password"
                        name="password"
                        value={formData.password}
                        onChange={handleInputChange}
                      />
                      <span className="password-toggle" onClick={togglePasswordVisibility} style={{ cursor: 'pointer' }}>
                        <i className={showPassword ? "uil uil-eye-slash" : "uil uil-eye"}></i>
                      </span>
                      <label htmlFor="password">{t('auth.password')}</label>
                      {errors.password && <div className="invalid-feedback">{errors.password}</div>}
                    </div>

                    {!isLoginMode && (
                      <div className="form-floating password-field mb-4">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          className={`form-control ${errors.confirmPassword ? 'is-invalid' : ''}`}
                          placeholder={t('auth.confirmPassword')}
                          id="confirmPassword"
                          name="confirmPassword"
                          value={formData.confirmPassword}
                          onChange={handleInputChange}
                        />
                        <span className="password-toggle" onClick={toggleConfirmPasswordVisibility} style={{ cursor: 'pointer' }}>
                          <i className={showConfirmPassword ? "uil uil-eye-slash" : "uil uil-eye"}></i>
                        </span>
                        <label htmlFor="confirmPassword">{t('auth.confirmPassword')}</label>
                        {errors.confirmPassword && <div className="invalid-feedback">{errors.confirmPassword}</div>}
                      </div>
                    )}

                    <button
                      type="submit"
                      className="btn btn-primary rounded-pill btn-login w-100 mb-2"
                      disabled={loading}
                    >
                      {loading ? t('common.loading') : (isLoginMode ? t('auth.loginButton') : t('auth.signupButton'))}
                    </button>
                  </form>

                  <p className="mb-0">
                    {isLoginMode ? (
                      <>
                        {t('auth.switchToSignup').split('?')[0]}? <button className="border-0 bg-transparent text-decoration-underline p-0 hover" onClick={switchMode}>{t('auth.signup')}</button>
                      </>
                    ) : (
                      <>
                        {t('auth.switchToLogin').split('?')[0]}? <button className="border-0 bg-transparent text-decoration-underline p-0 hover" onClick={switchMode}>{t('auth.login')}</button>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AuthForms;